/**
 * Dates cut in the agency's own time zone.
 *
 * An agency runs in one US time zone and the US spans several, so "Sep 17" is only an answer once you
 * know the zone (CLAUDE.md §Patterns, local time). This module is the single place that turns a
 * timestamp or a calendar date into a label, so the board and the billing page cut days the same way.
 */

/**
 * A `timestamptz` (an instant) labelled as a short month + day in `timeZone` — e.g. `Sep 17`. This is
 * the board's due/pickup label; the zone matters because the instant can fall on a different calendar
 * day in New York than in Los Angeles. Falls back to the browser zone when `timeZone` is undefined.
 * `locale` is the UI language (`i18n.language`): `es` reads `17 sept`. English by default.
 */
export function dateInZone(iso: string | null, timeZone: string | undefined, locale = 'en-US'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone }).format(d);
}

/** Like `dateInZone`, with the year: `Sep 25, 2026` (`25 sept 2026` in Spanish). invoice-pdf: when an
 *  invoice was emailed, which can be last year. */
export function dateYearInZone(iso: string | null, timeZone: string | undefined, locale = 'en-US'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone }).format(d);
}

/**
 * A plain `date` (a calendar day, `YYYY-MM-DD`) labelled as a short month + day — e.g. `Sep 17`. A
 * `date` carries no time, so it must NOT be shifted by a zone: `new Date('2026-09-17')` is UTC
 * midnight, which a US zone would render as the previous evening. It is formatted in UTC so the day
 * that was written is the day that shows. Invoice `issued_on` / `due_on` are already cut in the
 * agency's zone server-side (`(now() at time zone tz)::date`); this only renders them. `locale` as in
 * `dateInZone`.
 */
export function calendarDate(ymd: string | null, locale = 'en-US'): string {
  if (!ymd) return '';
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
}

/** Today's calendar day (`YYYY-MM-DD`) in `timeZone`. `en-CA` formats as `YYYY-MM-DD`. */
function todayInZone(timeZone: string | undefined, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(now);
}

/**
 * Today (`YYYY-MM-DD`) in the agency's zone: the `max` of a date input and the bound the backend
 * applies to a setup date ("not after today", cut in the agency zone). `now` is injectable for tests.
 */
export function localToday(timeZone: string, now: Date = new Date()): string {
  return todayInZone(timeZone, now);
}

/** The Intl locale for a UI language: US English, US Spanish (12-hour clock, as US drivers read it). */
function intlLocale(locale: string): string {
  if (locale.startsWith('es')) return 'es-US';
  if (locale.startsWith('en')) return 'en-US';
  return locale;
}

/**
 * An instant as weekday, month, day and time in `timeZone`, in the UI (or message) language: e.g.
 * `Fri, Sep 25, 2:00 PM`. The zone always comes from the data (the agency or the RPC), never from the
 * browser, so a dispatcher in another state reads the driver's deadline in the agency's clock.
 */
export function dateTimeInZone(iso: string | null, timeZone: string, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(d);
}

/** The city of an IANA zone, for "(Chicago time)": the last segment, underscores as spaces. */
export function zoneCity(timeZone: string): string {
  const last = timeZone.split('/').pop() ?? timeZone;
  return last.replace(/_/g, ' ');
}

/**
 * How many whole days past its due date an invoice is, measured against today in the agency's zone. A
 * due date of today or in the future returns 0 or a negative number: the caller shows the "N days
 * overdue" hint only when this is positive. Both days are compared as UTC calendar days so no zone
 * shift creeps in.
 */
export function daysOverdue(dueOn: string | null, timeZone: string | undefined): number {
  if (!dueOn) return 0;
  const due = new Date(`${dueOn}T00:00:00Z`);
  const today = new Date(`${todayInZone(timeZone)}T00:00:00Z`);
  if (Number.isNaN(due.getTime()) || Number.isNaN(today.getTime())) return 0;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((today.getTime() - due.getTime()) / MS_PER_DAY);
}
